import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Box, Button, CircularProgress } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useSession, useWebSocket } from '../hooks';
import DynamicBlockRenderer from '../components/editor/DynamicBlockRenderer';
import ExportButtons from '../components/export/ExportButtons';

export default function SessionEditor() {
  const { sessionId } = useParams();
  const { session, isLoading, updateSession } = useSession(sessionId);
  const [activeSequenceIndex, setActiveSequenceIndex] = useState(0);
  
  // WebSocket for real-time generation updates
  useWebSocket(sessionId, (update) => {
    if (update.type === 'generation_progress') {
      console.log(`Progress: ${update.sequence}/${update.total}`);
    }
    if (update.type === 'block_generated') {
      updateSession();
    }
    if (update.type === 'generation_complete') {
      updateSession();
    }
  });

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} sx={{ mb: 2, color: '#3498DB' }} />
          <Typography variant="h4" sx={{ ml: 2, color: '#2C3E50', fontWeight: 'bold' }}>
            Loading session...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!session) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ color: '#2C3E50', fontWeight: 'bold', textAlign: 'center' }}>
          Session not found
        </Typography>
      </Container>
    );
  }

  const activeSequence = session.sequences[activeSequenceIndex];

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            onClick={() => window.history.back()}
            startIcon={<ArrowBack />}
            variant="outlined"
            sx={{ 
              mb: 2,
              borderColor: '#FF9800',
              color: '#E65100',
              fontWeight: 'bold',
              '&:hover': {
                borderColor: '#F57C00',
                backgroundColor: '#FFF3E0'
              }
            }}
          >
            Back
          </Button>
          
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold', 
            color: '#E65100',
            mb: 1
          }}>
            📋 {session.title}
          </Typography>
          
          <Typography variant="h6" sx={{ color: '#7F8C8D', mb: 2 }}>
            Age: {session.config.age} | Level: {session.config.level} | Subject: {session.config.subject}
          </Typography>
          
          {/* Export buttons */}
          <ExportButtons sessionId={sessionId} session={session} />
        </Box>

        {/* Sequence Navigation */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
            Sequences ({session.sequences.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {session.sequences.map((sequence, index) => (
              <Button
                key={sequence.id}
                onClick={() => setActiveSequenceIndex(index)}
                variant={index === activeSequenceIndex ? 'contained' : 'outlined'}
                sx={{
                  borderRadius: '20px',
                  px: 2,
                  py: 1,
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  ...(index === activeSequenceIndex ? {
                    background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)'
                    }
                  } : {
                    borderColor: '#3498DB',
                    color: '#3498DB',
                    '&:hover': {
                      borderColor: '#2980B9',
                      backgroundColor: '#E8F4FD'
                    }
                  })
                }}
              >
                {index + 1}. {sequence.subTheme || sequence.title}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Current Sequence */}
        {activeSequence && (
          <Box>
            <Typography variant="h4" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
              Sequence {activeSequenceIndex + 1}: {activeSequence.subTheme || activeSequence.title}
            </Typography>
            
            {/* Blocks */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {(activeSequence.blocs || []).map((block, index) => (
                <Box 
                  key={block.id} 
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E0E0E0',
                    overflow: 'hidden'
                  }}
                >
                  {/* Block header */}
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#F8F9FA', 
                    borderBottom: '1px solid #E0E0E0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#6b7280' }}>
                        Block {index + 1}
                      </Typography>
                      <Box sx={{
                        px: 2,
                        py: 0.5,
                        backgroundColor: '#E3F2FD',
                        borderRadius: '12px',
                        border: '1px solid #BBDEFB'
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 'bold', 
                          color: '#1976d2',
                          textTransform: 'uppercase'
                        }}>
                          {block.type_bloc || block.type}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Block actions */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        size="small"
                        sx={{ 
                          color: '#6b7280',
                          fontSize: '0.7rem',
                          minWidth: 'auto',
                          px: 1
                        }}
                        onClick={() => console.log('Move up:', block.id)}
                      >
                        ↑
                      </Button>
                      <Button 
                        size="small"
                        sx={{ 
                          color: '#6b7280',
                          fontSize: '0.7rem',
                          minWidth: 'auto',
                          px: 1
                        }}
                        onClick={() => console.log('Move down:', block.id)}
                      >
                        ↓
                      </Button>
                      <Button 
                        size="small"
                        sx={{ 
                          color: '#dc2626',
                          fontSize: '0.7rem',
                          minWidth: 'auto',
                          px: 1
                        }}
                        onClick={() => console.log('Delete:', block.id)}
                      >
                        🗑️
                      </Button>
                    </Box>
                  </Box>
                  
                  {/* Dynamic block rendering */}
                  <DynamicBlockRenderer 
                    block={block}
                    sessionId={sessionId}
                    sequenceIndex={activeSequenceIndex}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </div>
  );
}
